import React from "react";
import Reveal from "./Reveal";
import { ArrowRight } from "lucide-react";

/**
 * Founder Stripe.
 *
 * Compact first-person Cyrus voice that sits between Hero and
 * WhereDoYouFit. Replaces the full-length FounderNoteSection
 * letter with a single distilled paragraph + signature + two
 * quiet links so the homepage keeps the founder soul without the
 * vertical real estate of the old letter card.
 *
 * Layout: portrait left, text right on lg+, stacked on mobile.
 * The portrait sits inside a gold-ring treatment that mirrors the
 * Cyrus Profile page so visual identity stays continuous between
 * the two surfaces.
 */
const PORTRAIT = "/assets/brand/cyrus-portrait.webp";

const FounderStripe = () => (
  <section
    id="founder-stripe"
    data-testid="founder-stripe-section"
    className="relative py-16 md:py-20 border-t border-gold/10"
  >
    <div className="max-w-5xl mx-auto px-6 md:px-10">
      <Reveal>
        <article
          data-testid="founder-stripe-card"
          className="bg-white/5 border-t border-gold/20 p-7 md:p-10 rounded-lg border border-gold/10 grid grid-cols-1 lg:grid-cols-[120px_1fr] gap-6 md:gap-10 items-start"
        >
          <div className="mx-auto lg:mx-0 relative w-[120px]">
            <div
              className="absolute -inset-2 rounded-full blur-xl opacity-50 pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(212,175,55,0.35), transparent 70%)" }}
              aria-hidden
            />
            <div className="relative rounded-full p-[1.5px] bg-gradient-to-br from-[#F3E5AB] via-[#D4AF37] to-[#7A5F12]">
              <div className="rounded-full overflow-hidden bg-black/40">
                <img
                  src={PORTRAIT}
                  alt="Cyrus Prescod"
                  data-testid="founder-stripe-portrait"
                  className="block w-full aspect-square object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>

          <div>
            <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-3">
              A Note from the Founder
            </p>
            <p
              data-testid="founder-stripe-body"
              className="text-white/90 text-[15.5px] md:text-[17px] leading-relaxed font-serif"
            >
              I built this the slow way. Years of pushing harder when I should have paused, restarting the same plans, and repeating patterns I could not yet see in myself. What finally changed things was not more motivation. It was learning to read the pattern honestly and give it structure. Authentic Momentum is the practical framework that came out of those years, built so you can recognize your own pattern sooner and turn what you are carrying into a real next move.
            </p>
            <p
              data-testid="founder-stripe-signature"
              className="mt-5 font-serif text-themed text-xl italic gold-gradient-text leading-none"
            >
              Cyrus Prescod
            </p>
            <p className="mt-1 text-[10px] tracking-[0.4em] uppercase text-gold/80">
              Founder, Authentic Momentum
            </p>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
              <a
                href="/cyrus-prescod"
                data-testid="founder-stripe-link-profile"
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-gold hover:text-[#F3E5AB] transition-colors"
              >
                Founder Profile <ArrowRight size={12} />
              </a>
              <a
                href="/about"
                data-testid="founder-stripe-link-about"
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-gold/75 hover:text-gold transition-colors"
              >
                Read the Full Story <ArrowRight size={12} />
              </a>
            </div>
          </div>
        </article>
      </Reveal>
    </div>
  </section>
);

export default FounderStripe;
